import type { ApiError } from '@/types/api/mutations';
import type { AxiosError } from 'axios';

import { DisplayToast } from './displayToast';

/**
 * Extracts the most informative message from a mutation / query error.
 * Looks at (in order):
 *   - `response.data.error.message` (403 access-denied, 400 validation, misc)
 *   - `response.data.errors[].message` (RecordInvalid / RecordNotDestroyed shape)
 *   - `response.data.flash_message`  (error_json_response shape)
 * Returns `undefined` if nothing useful is present — caller can fall back
 * to a default toast.
 */
export const getMutationErrorMessage = (error: unknown): string | undefined => {
  const axiosError = error as AxiosError;
  const responseData = axiosError.response?.data as
    | Record<string, unknown>
    | undefined;
  const errorObj = responseData?.error as { message?: string } | undefined;
  const errors = responseData?.errors;
  const firstError = Array.isArray(errors)
    ? (errors as ApiError[]).find((item) => item?.message)
    : undefined;
  return (
    errorObj?.message ||
    firstError?.message ||
    (responseData?.flash_message as string | undefined)
  );
};

/**
 * Shows an error toast with the extracted message (or the default fallback
 * inside `DisplayToast.error`).
 */
export const notifyMutationError = (error: unknown) => {
  DisplayToast.error(getMutationErrorMessage(error));
};
